import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useProject } from "../contexts/ProjectContext";
import { SelfieThumb } from "./SelfieThumb";
import { unitsBought } from "../lib/surveyFmt";
import { fmtTime, fmtDate } from "../lib/time";
import { Camera, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";

// Per-store collage of check-in/check-out selfies from shifts started in the last 24h.
export default function StoreSelfieCollages() {
  const { projectId } = useProject();
  const [shifts, setShifts] = useState([]);
  const [missing, setMissing] = useState([]);
  const [shiftStats, setShiftStats] = useState({});
  const [open, setOpen] = useState(() => window.innerWidth >= 640);

  useEffect(() => {
    const cutoff = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
    const params = new URLSearchParams({ from: cutoff });
    const covParams = new URLSearchParams();
    if (projectId && projectId !== "__all") {
      params.set("project_id", projectId);
      covParams.set("project_id", projectId);
    }
    const load = () => {
      api
        .get(`/admin/shifts?${params.toString()}`)
        .then((r) => setShifts(r.data || []))
        .catch(() => {});
      api
        .get(`/admin/coverage/today?${covParams.toString()}`)
        .then((r) => setMissing((r.data?.rows || []).filter((x) => !x.checked_in)))
        .catch(() => {});
      api
        .get(`/admin/surveys?${params.toString()}`)
        .then((r) => {
          const acc = {};
          for (const s of r.data || []) {
            const sid = s.shift_id;
            if (!sid) continue;
            const a = (acc[sid] = acc[sid] || { units: 0, surveys: 0, buyers: 0 });
            const u = unitsBought(s);
            a.units += u;
            a.surveys += 1;
            if (u > 0) a.buyers += 1;
          }
          setShiftStats(acc);
        })
        .catch(() => {});
    };
    load();
    const t = setInterval(() => {
      if (!document.hidden) load();
    }, 120000);
    return () => clearInterval(t);
  }, [projectId]);

  const byStore = useMemo(() => {
    const cutoffMs = Date.now() - 24 * 3600 * 1000;
    const groups = {};
    for (const s of shifts) {
      if (!s.check_in_photo && !s.check_out_photo) continue;
      if (!s.check_in_at || new Date(s.check_in_at).getTime() < cutoffMs) continue;
      const store = s.store?.name || "Unknown store";
      (groups[store] = groups[store] || []).push(s);
    }
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [shifts]);

  if (byStore.length === 0 && missing.length === 0) return null;

  return (
    <div data-testid="store-selfie-collages">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 mb-3 group"
        data-testid="selfies-toggle"
      >
        <Camera className="h-4 w-4 text-neutral-400" />
        <h2 className="font-display font-bold text-xl tracking-tight">Shift selfies</h2>
        <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-medium">
          last 24 hours
        </span>
        {missing.length > 0 && (
          <span className="text-[10px] uppercase tracking-widest font-semibold text-red-600 bg-red-50/80 border border-red-200 rounded-full px-2 py-0.5">
            {missing.length} not reached
          </span>
        )}
        <span className="ml-auto w-8 h-8 rounded-full bg-white/60 border border-white/60 shadow-sm flex items-center justify-center text-slate-500 group-hover:bg-white/85">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {open && (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {missing.length > 0 && (
          <div
            className="glass-panel p-4"
            style={{ background: "rgba(254, 242, 242, 0.75)" }}
            data-testid="missing-checkins-card"
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <div className="font-semibold text-sm text-red-700">
                Not checked in today ({missing.length})
              </div>
            </div>
            <div className="mt-3 space-y-2.5">
              {missing.map((m, i) => (
                <div
                  key={`${m.promoter_id}-${m.store_id}`}
                  className="flex items-center justify-between gap-2 rounded-xl bg-white/60 border border-white/60 px-3 py-2"
                  data-testid={`missing-row-${i}`}
                >
                  <div className="min-w-0">
                    <div className="text-xs font-semibold truncate">{m.store_name}</div>
                    <div className="text-[10px] text-neutral-500 truncate">{m.promoter_name}</div>
                  </div>
                  <div className="text-[10px] font-mono text-red-500 shrink-0">
                    {m.start && m.end ? `${m.start}–${m.end}` : "scheduled"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {byStore.map(([store, rows]) => (
          <div key={store} className="glass-panel p-4" data-testid={`selfie-collage-${store}`}>
            <div className="font-semibold text-sm truncate">{store}</div>
            <div className="mt-3 space-y-3">
              {rows.map((s) => (
                <div key={s.id} className="flex items-center gap-3">
                  <div className="flex gap-2">
                    <div className="text-center">
                      <SelfieThumb path={s.check_in_photo} size={72} alt="Check-in" />
                      <div className="text-[9px] uppercase tracking-widest text-emerald-600 font-semibold mt-1">
                        In {s.check_in_at ? fmtTime(s.check_in_at) : ""}
                      </div>
                    </div>
                    <div className="text-center">
                      {s.check_out_photo ? (
                        <>
                          <SelfieThumb path={s.check_out_photo} size={72} alt="Check-out" />
                          <div className="text-[9px] uppercase tracking-widest text-red-500 font-semibold mt-1">
                            Out {s.check_out_at ? fmtTime(s.check_out_at) : ""}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="w-[72px] h-[72px] rounded-xl border border-dashed border-slate-300/80 flex items-center justify-center text-[9px] uppercase tracking-widest text-slate-400">
                            On shift
                          </div>
                          <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold mt-1">
                            Active
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold truncate">{s.promoter?.name || "—"}</div>
                    <div className="text-[10px] text-neutral-500">
                      {s.check_in_at ? fmtDate(s.check_in_at) : ""}
                    </div>
                    {(() => {
                      const st = shiftStats[s.id] || { units: 0, surveys: 0, buyers: 0 };
                      const tu = s.store?.target_units || 0;
                      const tc = s.store?.target_conversion || 0;
                      const conv = st.surveys ? Math.round((st.buyers / st.surveys) * 1000) / 10 : 0;
                      return (
                        <div className="mt-1 space-y-0.5" data-testid={`shift-targets-${s.id}`}>
                          <div
                            className={`text-[10px] font-mono font-semibold ${
                              tu > 0 ? (st.units >= tu ? "text-emerald-600" : "text-amber-600") : "text-slate-600"
                            }`}
                          >
                            Sales {st.units}
                            {tu > 0 && ` / ${tu}`}
                          </div>
                          <div
                            className={`text-[10px] font-mono font-semibold ${
                              tc > 0 ? (conv >= tc ? "text-emerald-600" : "text-amber-600") : "text-slate-600"
                            }`}
                          >
                            Conv {conv}%{tc > 0 && ` / ${tc}%`}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}
